import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, SearchIcon, ReceiptIcon, FilterIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { InvoiceCard, Invoice } from '../components/InvoiceCard';
import { AddInvoiceModal } from '../components/AddInvoiceModal';
import { EditInvoiceModal } from '../components/EditInvoiceModal';
interface InvoicesProps {
  invoices: Invoice[];
  onAddInvoice: (invoice: Omit<Invoice, 'id'>) => void;
  onEditInvoice: (invoice: Invoice) => void;
  onDeleteInvoice: (invoice: Invoice) => void;
  canAddInvoice: boolean;
  customers: Array<{
    id: string;
    customerId: string;
    name: string;
  }>;
}
export function Invoices({
  invoices,
  onAddInvoice,
  onEditInvoice,
  onDeleteInvoice,
  canAddInvoice,
  customers
}: InvoicesProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [paidFilter, setPaidFilter] = useState<string>('all');
  const isReadOnly = !canAddInvoice;
  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const matchesSearch = invoice.supplier.toLowerCase().includes(searchQuery.toLowerCase()) || invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) || invoice.customerName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || invoice.category === categoryFilter;
      const matchesPaid = paidFilter === 'all' || paidFilter === 'paid' && invoice.paid || paidFilter === 'unpaid' && !invoice.paid;
      return matchesSearch && matchesCategory && matchesPaid;
    });
  }, [invoices, searchQuery, categoryFilter, paidFilter]);
  // Calculate stats
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidAmount = invoices.filter(inv => inv.paid).reduce((sum, inv) => sum + inv.amount, 0);
  const unpaidAmount = totalAmount - paidAmount;
  return <div className="space-y-6" data-id="element-1483">
      {/* Header */}
      <header className="flex items-center justify-between" data-id="element-1484">
        <div data-id="element-1485">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1486">Fakturaer</h1>
          <p className="text-stone-600" data-id="element-1487">
            {isReadOnly ? 'Se dine indkøbsfakturaer' : 'Administrer dine indkøbsfakturaer'}
          </p>
        </div>
        {canAddInvoice && <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2" data-id="element-1488">
            <PlusIcon className="w-5 h-5" data-id="element-1489" />
            Tilføj faktura
          </Button>}
      </header>

      {/* Stats */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1490">
        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1491">
          <div className="flex items-center gap-3 mb-2" data-id="element-1492">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1493">
              <ReceiptIcon className="w-5 h-5 text-blue-700" data-id="element-1494" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1495">Total beløb</p>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1496">
            {totalAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1497">
            {invoices.length} fakturaer
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1498">
          <div className="flex items-center gap-3 mb-2" data-id="element-1499">
            <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-1500">
              <ReceiptIcon className="w-5 h-5 text-emerald-700" data-id="element-1501" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1502">Betalt</p>
          </div>
          <p className="text-3xl font-bold text-emerald-700" data-id="element-1503">
            {paidAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1504">
            {invoices.filter(inv => inv.paid).length} fakturaer
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1505">
          <div className="flex items-center gap-3 mb-2" data-id="element-1506">
            <div className="p-2 bg-red-100 rounded-lg" data-id="element-1507">
              <ReceiptIcon className="w-5 h-5 text-red-700" data-id="element-1508" />
            </div>
            <p className="text-sm font-medium text-stone-600" data-id="element-1509">Ubetalt</p>
          </div>
          <p className="text-3xl font-bold text-red-700" data-id="element-1510">
            {unpaidAmount.toLocaleString('da-DK')} kr
          </p>
          <p className="text-sm text-stone-500 mt-1" data-id="element-1511">
            {invoices.filter(inv => !inv.paid).length} fakturaer
          </p>
        </div>
      </motion.div>

      {/* Search and Filters */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-id="element-1512">
        <div className="flex items-center gap-3 mb-4" data-id="element-1513">
          <FilterIcon className="w-5 h-5 text-stone-600" data-id="element-1514" />
          <h2 className="text-lg font-semibold text-stone-900" data-id="element-1515">
            Søg og filtrer
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1516">
          <div className="relative" data-id="element-1517">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1518" />
            <input type="text" placeholder="Søg efter leverandør, faktura nr. eller kunde..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all" data-id="element-1519" />
          </div>

          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all" data-id="element-1520">
            <option value="all" data-id="element-1521">Alle kategorier</option>
            <option value="Sikkerhed" data-id="element-1522">Sikkerhed</option>
            <option value="Værktøj" data-id="element-1523">Værktøj</option>
            <option value="Materialer" data-id="element-1524">Materialer</option>
            <option value="Andet" data-id="element-1525">Andet</option>
          </select>

          <select value={paidFilter} onChange={e => setPaidFilter(e.target.value)} className="px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all" data-id="element-1526">
            <option value="all" data-id="element-1527">Alle status</option>
            <option value="paid" data-id="element-1528">Betalt</option>
            <option value="unpaid" data-id="element-1529">Ubetalt</option>
          </select>
        </div>
      </motion.div>

      {/* Invoice List */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} data-id="element-1530">
        {filteredInvoices.length === 0 ? <div className="bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1531">
            <ReceiptIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1532" />
            <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1533">
              {searchQuery || categoryFilter !== 'all' || paidFilter !== 'all' ? 'Ingen fakturaer matcher dine filtre' : 'Ingen fakturaer endnu'}
            </h3>
            <p className="text-stone-500 mb-6" data-id="element-1534">
              {searchQuery || categoryFilter !== 'all' || paidFilter !== 'all' ? 'Prøv at justere dine søgekriterier' : isReadOnly ? 'Der er ingen fakturaer i systemet' : 'Tilføj din første faktura for at komme i gang'}
            </p>
            {!searchQuery && categoryFilter === 'all' && paidFilter === 'all' && canAddInvoice && <Button onClick={() => setIsAddModalOpen(true)} data-id="element-1535">
                  Tilføj faktura
                </Button>}
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1536">
            {filteredInvoices.map((invoice, index) => <InvoiceCard key={invoice.id} invoice={invoice} index={index} onEdit={setEditingInvoice} onDelete={onDeleteInvoice} readOnly={isReadOnly} data-id="element-1537" />)}
          </div>}
      </motion.div>

      {canAddInvoice && <>
          <AddInvoiceModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={onAddInvoice} customers={customers} data-id="element-1538" />

          <EditInvoiceModal isOpen={!!editingInvoice} onClose={() => setEditingInvoice(null)} onSave={onEditInvoice} invoice={editingInvoice} customers={customers} data-id="element-1539" />
        </>}
    </div>;
}